import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useTenant } from '@/contexts/TenantContext';
import { TenantService } from '@/services/tenantService';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload, Palette, BarChart3 } from 'lucide-react';
import { LogoUpload } from '../admin/LogoUpload';
import { TenantLeadExport } from './TenantLeadExport';
import { TenantLeadStatusList } from './TenantLeadStatusList';
import { BannerConfigManager } from '../admin/BannerConfigManager';
import { useLeadInsertNotifications } from '@/hooks/useLeadInsertNotifications';

interface TenantStats {
  leadsCount: number;
  submissionsCount: number;
  productsCount: number;
}

export const TenantDashboard: React.FC = () => {
  const { tenant, refreshTenant } = useTenant();
  const { toast } = useToast();
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Form states
  const [logoUrl, setLogoUrl] = useState('');
  const [colorScheme, setColorScheme] = useState({
    primary: '#007bff',
    secondary: '#6c757d',
    accent: '#28a745',
    background: '#ffffff',
    text: '#212529'
  });

  const fetchStats = useCallback(async () => {
    if (!tenant) return;

    try {
      const tenantStats = await TenantService.getTenantStats(tenant.id);
      setStats(tenantStats);
    } catch (error) {
      console.error('Failed to fetch tenant stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard statistics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [tenant, toast]);

  useEffect(() => {
    if (tenant) {
      setLogoUrl(tenant.logoUrl || '');
      if (tenant.colorScheme) {
        setColorScheme(prev => ({ ...prev, ...tenant.colorScheme }));
      }
      fetchStats();
    }
  }, [tenant, fetchStats]);

  // Tenant-specific live notification only for this tenant
  useLeadInsertNotifications({ tenantId: tenant?.id });

  const handleUpdateLogo = async () => {
    if (!tenant || !logoUrl.trim()) return;
    
    setUpdating(true);
    try {
      await TenantService.updateTenantLogo(tenant.id, logoUrl.trim());
      await refreshTenant();
      toast({
        title: 'Success',
        description: 'Logo updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update logo',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateColorScheme = async () => {
    if (!tenant) return;
    
    setUpdating(true);
    try {
      await TenantService.updateTenantColorScheme(tenant.id, colorScheme);
      await refreshTenant();
      toast({
        title: 'Success',
        description: 'Color scheme updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update color scheme',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  if (!tenant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">No Tenant Configuration</h2>
          <p className="text-muted-foreground">Unable to load tenant information</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Tenant Dashboard</h1>
        <p className="text-muted-foreground">
          Manage your tenant settings and view analytics for <Badge variant="outline">{tenant.name}</Badge>
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Aesthetic card glow using tenant colors */}
        <style>{`
          .tenant-card-glow {
            border: 1px solid color-mix(in srgb, var(--tenant-primary) 25%, transparent);
            box-shadow: 0 6px 24px color-mix(in srgb, var(--tenant-primary) 20%, transparent);
            border-radius: 12px;
          }
        `}</style>
        <Card className="tenant-card-glow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats?.leadsCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Leads generated for your tenant
            </p>
          </CardContent>
        </Card>

        <Card className="tenant-card-glow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Submissions</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats?.submissionsCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Form submissions received
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Products</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats?.productsCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Available insurance products
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo Settings */}
        <Card className="tenant-card-glow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Logo Settings
            </CardTitle>
            <CardDescription>
              Update your tenant's logo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LogoUpload
              value={logoUrl}
              onChange={(url) => {
                setLogoUrl(url);
                handleUpdateLogo();
              }}
              tenantName={tenant.name}
            />
          </CardContent>
        </Card>

        {/* Color Scheme Settings */}
        <Card className="tenant-card-glow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Color Scheme
            </CardTitle>
            <CardDescription>
              Customize your tenant's color palette
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary-color">Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="primary-color"
                    type="color"
                    value={colorScheme.primary}
                    onChange={(e) => setColorScheme({ ...colorScheme, primary: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    type="text"
                    value={colorScheme.primary}
                    onChange={(e) => setColorScheme({ ...colorScheme, primary: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="secondary-color">Secondary Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="secondary-color"
                    type="color"
                    value={colorScheme.secondary}
                    onChange={(e) => setColorScheme({ ...colorScheme, secondary: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    type="text"
                    value={colorScheme.secondary}
                    onChange={(e) => setColorScheme({ ...colorScheme, secondary: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="accent-color">Accent Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="accent-color"
                    type="color"
                    value={colorScheme.accent}
                    onChange={(e) => setColorScheme({ ...colorScheme, accent: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    type="text"
                    value={colorScheme.accent}
                    onChange={(e) => setColorScheme({ ...colorScheme, accent: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="text-color">Text Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="text-color"
                    type="color"
                    value={colorScheme.text}
                    onChange={(e) => setColorScheme({ ...colorScheme, text: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    type="text"
                    value={colorScheme.text}
                    onChange={(e) => setColorScheme({ ...colorScheme, text: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            
            <Button 
              onClick={handleUpdateColorScheme} 
              disabled={updating}
              className="w-full"
            >
              {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Color Scheme
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Banner Configuration Section */}
      <BannerConfigManager
        initialConfig={tenant?.bannerConfig}
        onSave={async (bannerConfig) => {
          if (!tenant) return;
          await TenantService.updateTenant(tenant.id, { banner_config_json: bannerConfig });
          await refreshTenant();
        }}
        loading={updating}
      />

      {/* Lead Export Section */}
      <TenantLeadExport />

      {/* Live Lead Progress (read-only) */}
      <TenantLeadStatusList />

      {/* Tenant Information */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Information</CardTitle>
          <CardDescription>
            Basic information about your tenant instance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Tenant Name</Label>
              <p className="text-sm text-muted-foreground mt-1">{tenant.name}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Subdomain</Label>
              <p className="text-sm text-muted-foreground mt-1">{tenant.subdomain}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Tenant ID</Label>
              <p className="text-sm text-muted-foreground mt-1 font-mono">{tenant.id}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};